import { CapacitorHttp } from '@capacitor/core';

import { toRelayDateTime } from '../persistence/relay';
import { meldeNetzfehler } from './netzfehler';
import { verbindung } from './verbindung';
import { appendAppLog } from '../platform/appLog';
import { verarbeiteServerversatz } from '../platform/clockCheck';
import { istGeraet } from '../platform/gleis';

/**
 * Der Weg nach draussen — **die einzige Stelle der App mit `fetch`**.
 *
 * Alles darüber (Katalog, Übertragung, Nachrichten) spricht mit diesem Modul, nie mit dem Netz.
 * Damit lässt sich der ganze Rest ohne Netz prüfen: die Tests schieben ein eigenes `fetch`
 * unter (`ServerDeps.fetch`), und niemand muss einen Server starten.
 *
 * Was hier **nicht** passiert: die Zertifikatsprüfung wird nirgends ausgehebelt, auch nicht
 * hinter einem Schalter „nur für Tests". Relay liefert seine eigene CA unter
 * `/rest/v1/ca/crt` aus; ein iPad muss ihr über ein Profil vertrauen. Das ist Betrieb, nicht
 * Code — und der einzige richtige Weg (s. `docs/spec-phase4-server.md` §5).
 */

/** Was die App über sich selbst sagt, wenn sie sich anmeldet. */
export interface Geraeteangaben {
  deviceIdentifier: string;
  deviceName: string;
  /** Relay führt `Client.clientType` und `ClientSetting.clientType` getrennt — hier gilt ersterer. */
  clientType: string;
  appType: string;
  platformName: string;
  /** Pflicht bei Relay seit `e964dbe`: „0.1.0 (1)“. */
  appVersion: string;
  /** Reine Versionsnummer, z. B. „18.5“; Pflicht wie `appVersion`. */
  osVersion: string;
}

export interface Zugang {
  baseUrl: string;
  login: string;
  password: string;
}

/**
 * Eine Antwort, so viel davon wie dieses Modul braucht. Absichtlich **nicht** `Response`: auf
 * dem Gerät kommt sie aus `CapacitorHttp` und ist keine.
 */
export interface Antwort {
  status: number;
  ok: boolean;
  text(): Promise<string>;
}

/** Wie ein Aufruf hinausgeht. Wirft bei Netzfehlern, statt sie als Antwort zu verkleiden. */
export type Transport = (
  url: string,
  init: { method: string; headers?: Record<string, string>; body?: string; timeoutMs: number },
) => Promise<Antwort>;

export interface ServerDeps {
  zugang: Zugang;
  geraet: Geraeteangaben;
  /** Einschiebbar für Tests; sonst das `fetch` der Laufzeit. */
  fetch?: typeof fetch;
  /** Einschiebbar für Tests; sonst nativ (Gerät) bzw. `fetch` (Browser). */
  transport?: Transport;
  /** Zeitgrenze je Aufruf. */
  timeoutMs?: number;
  /** Nur für Tests: feste Uhr. */
  jetzt?: () => Date;
}

/** Was die Anmeldung zurückgibt — `driftSeconds` ist der Versatz Gerät gegen Server. */
export interface Anmeldung {
  token: string;
  clientId: string;
  role: string;
  serverTime: string;
  driftSeconds: number;
}

export type FehlerArt =
  /** Kein Netz, falsche Adresse, Zertifikat nicht vertraut — nichts hat geantwortet. */
  | 'keineVerbindung'
  /** Die Antwort blieb aus, bis die Zeitgrenze lief. */
  | 'zeitueberschreitung'
  /** Der Server hat geantwortet und abgelehnt (4xx), `code` trägt seinen Fehlercode. */
  | 'abgelehnt'
  /** Der Server ist da, aber kaputt (5xx). */
  | 'serverfehler';

export class ServerFehler extends Error {
  constructor(
    readonly art: FehlerArt,
    message: string,
    readonly status?: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ServerFehler';
  }
}

/** Ob ein erneuter Versuch überhaupt Sinn hat. */
export function lohntWiederholung(fehler: unknown): boolean {
  if (!(fehler instanceof ServerFehler)) return false;
  return fehler.art !== 'abgelehnt';
}

export const STANDARD_TIMEOUT_MS = 15000;

/**
 * Der Weg über `fetch` — Browser und Tests.
 */
export function fetchTransport(hole: typeof fetch): Transport {
  return async (url, init) => {
    const abbruch = new AbortController();
    const wecker = setTimeout(() => abbruch.abort(), init.timeoutMs);
    try {
      const antwort = await hole(url, {
        method: init.method,
        headers: init.headers,
        body: init.body,
        signal: abbruch.signal,
      });
      return { status: antwort.status, ok: antwort.ok, text: () => antwort.text() };
    } finally {
      clearTimeout(wecker);
    }
  };
}

/**
 * Der Weg über `CapacitorHttp` — auf dem Gerät.
 *
 * ⚠️ **Warum nicht einfach `fetch`?** Am 2026-09-11 auf dem Simulator gemessen: aus dem
 * WKWebView heraus ist **jeder** Serveraufruf fremdherkünftig — die App läuft unter
 * `capacitor://localhost`. Ein Server ohne CORS-Kopfzeilen wird mit `TypeError: Load failed`
 * abgewiesen, ununterscheidbar von „Server aus". Relay müsste also für diese App CORS
 * beantworten; mit demselben Server, nur um `Access-Control-Allow-Origin` ergänzt, ging
 * derselbe Aufruf durch.
 *
 * Der native Weg kennt keine Herkunftsprüfung und keinen Vorflug. Ein Fehlschlag kommt dort
 * als Fehlschlag zurück (gemessen: `Error: Could not connect to the server.`), nicht als
 * Antwort.
 *
 * ⚠️ **Beim Messen aufgepasst:** lief auf dem Entwicklungsrechner ein abfangender Proxy
 * (Charles), beantwortete **er** unerreichbare Ziele mit `503` und einer HTML-Seite — die App
 * meldete dann „Server antwortete 503", obwohl nichts erreichbar war. Das ist kein Verhalten
 * von Capacitor. Wer solche Fehler nachstellt, sieht zuerst nach, ob ein Proxy dazwischenhängt.
 */
export function nativerTransport(): Transport {
  return async (url, init) => {
    const antwort = await CapacitorHttp.request({
      url,
      method: init.method,
      headers: init.headers,
      data: init.body,
      // Der Rumpf soll als Text durchgereicht werden, nicht vom Plugin gedeutet: die
      // Fehlerbehandlung hier liest ihn selbst und darf sich auf nichts anderes verlassen.
      responseType: 'text',
      connectTimeout: init.timeoutMs,
      readTimeout: init.timeoutMs,
    });
    const rumpf = typeof antwort.data === 'string' ? antwort.data : JSON.stringify(antwort.data ?? null);
    return {
      status: antwort.status,
      ok: antwort.status >= 200 && antwort.status < 300,
      text: async () => rumpf,
    };
  };
}

export interface ServerClient {
  /** Meldet an, falls noch kein Token vorliegt, und gibt die Angaben der Anmeldung zurück. */
  anmelden(): Promise<Anmeldung>;
  /** Lebenszeichen; wirft wie jeder andere Aufruf. */
  health(): Promise<unknown>;
  get<T>(
    pfad: string,
    query?: Record<string, string | number | undefined>,
    kopf?: Record<string, string>,
  ): Promise<T>;
  post<T>(pfad: string, rumpf: unknown): Promise<T>;
  /** Der Stand der letzten Anmeldung, ohne eine neue auszulösen. */
  letzteAnmeldung(): Anmeldung | null;
  /** Wirft das Token weg — nach einem Wechsel der Zugangsdaten. */
  vergessen(): void;
}

export function createServerClient(deps: ServerDeps): ServerClient {
  const transport: Transport =
    deps.transport ??
    (deps.fetch
      ? fetchTransport(deps.fetch)
      : istGeraet()
        ? nativerTransport()
        : fetchTransport(globalThis.fetch.bind(globalThis)));
  const timeoutMs = deps.timeoutMs ?? STANDARD_TIMEOUT_MS;
  const jetzt = deps.jetzt ?? (() => new Date());
  const basis = deps.zugang.baseUrl.replace(/\/+$/, '');

  let token: string | null = null;
  let anmeldung: Anmeldung | null = null;
  /**
   * Zwei gleichzeitige Aufrufe sollen sich **eine** Anmeldung teilen. Ohne das melden sich beim
   * Start drei Abfragen gleichzeitig an, und Relay zählt jede davon auf die Ratenbegrenzung
   * (`SessionService`: zwei Schlüssel, Gerät und IP).
   */
  let laufendeAnmeldung: Promise<Anmeldung> | null = null;

  const url = (pfad: string, query?: Record<string, string | number | undefined>) => {
    const p = pfad.startsWith('/') ? pfad : '/' + pfad;
    const teile = Object.entries(query ?? {})
      .filter(([, wert]) => wert !== undefined && wert !== '')
      .map(([k, wert]) => encodeURIComponent(k) + '=' + encodeURIComponent(String(wert)));
    return basis + p + (teile.length > 0 ? '?' + teile.join('&') : '');
  };

  /**
   * Ein Aufruf samt Zeitgrenze und Fehlerübersetzung. Ohne eigene Zeitgrenze hängt ein Aufruf
   * im Prüfungsbetrieb beliebig lange — das WLAN ist dort die unzuverlässigste Schicht, und
   * die Oberfläche darf nicht warten, bis das Betriebssystem irgendwann aufgibt.
   */
  const roh = async (
    pfad: string,
    init: { method: string; headers?: Record<string, string>; body?: string },
    query?: Record<string, string | number | undefined>,
  ): Promise<Antwort> => {
    try {
      const antwort = await transport(url(pfad, query), { ...init, timeoutMs });
      // Geantwortet hat er — gleich was. Das beendet eine Offline-Phase (`verbindung.ts`).
      verbindung.erfolg();
      return antwort;
    } catch (fehler) {
      // Nur der erste Ausfall einer Offline-Phase kommt ins Log; die folgenden zählt die Lage.
      const ersterAusfall = verbindung.ausfall();
      // `AbortError` heisst hier immer „Zeitgrenze": abgebrochen wird nur vom Wecker oben.
      const abgebrochen =
        typeof fehler === 'object' && fehler !== null && (fehler as { name?: string }).name === 'AbortError';
      // Apples Rohmeldung sagt nur „Could not connect to the server." — was das heisst, steht
      // danach als eigene Zeile daneben.
      if (!abgebrochen && ersterAusfall) meldeNetzfehler(fehler);
      throw new ServerFehler(
        abgebrochen ? 'zeitueberschreitung' : 'keineVerbindung',
        abgebrochen
          ? 'Der Server hat nicht innerhalb von ' + String(Math.round(timeoutMs / 1000)) + ' s geantwortet.'
          : 'Keine Verbindung zum Server: ' + String(fehler),
      );
    }
  };

  /** Aus einer abgelehnten Antwort den Relay-Fehlercode holen, ohne am Rumpf zu scheitern. */
  const fehlerAus = async (antwort: Antwort): Promise<ServerFehler> => {
    let code: string | undefined;
    let text = '';
    try {
      text = await antwort.text();
      const gelesen: unknown = JSON.parse(text);
      if (typeof gelesen === 'object' && gelesen !== null) {
        const g = gelesen as { code?: unknown; error?: unknown };
        // Relay schickt {"error":{"code","message"}}; ältere Antworten trugen den Code oben.
        const k = g.code ?? (g.error as { code?: unknown } | null)?.code ?? g.error;
        if (typeof k === 'string') code = k;
      }
    } catch {
      // Kein JSON — der Text bleibt, wie er kam.
    }
    const art: FehlerArt = antwort.status >= 500 ? 'serverfehler' : 'abgelehnt';
    return new ServerFehler(
      art,
      'Server antwortete ' + String(antwort.status) + (code ? ' (' + code + ')' : '') +
        (text && !code ? ': ' + text.slice(0, 200) : ''),
      antwort.status,
      code,
    );
  };

  const anmelden = async (): Promise<Anmeldung> => {
    if (anmeldung && token) return anmeldung;
    if (laufendeAnmeldung) return laufendeAnmeldung;

    laufendeAnmeldung = (async () => {
      const antwort = await roh('/rest/v1/login/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          login: deps.zugang.login,
          password: deps.zugang.password,
          deviceIdentifier: deps.geraet.deviceIdentifier,
          deviceName: deps.geraet.deviceName,
          clientType: deps.geraet.clientType,
          // Relay rechnet daraus den Versatz und warnt ab 30 s (`memory/client-time-sync.md`).
          clientTime: toRelayDateTime(jetzt()),
          appType: deps.geraet.appType,
          platformName: deps.geraet.platformName,
          appVersion: deps.geraet.appVersion,
          osVersion: deps.geraet.osVersion,
        }),
      });
      if (!antwort.ok) throw await fehlerAus(antwort);
      const gelesen = JSON.parse(await antwort.text()) as Partial<Anmeldung>;
      if (!gelesen.token) {
        throw new ServerFehler('serverfehler', 'Die Anmeldung kam ohne Token zurück.');
      }
      anmeldung = {
        token: gelesen.token,
        clientId: gelesen.clientId ?? '',
        role: gelesen.role ?? '',
        serverTime: gelesen.serverTime ?? '',
        driftSeconds: gelesen.driftSeconds ?? 0,
      };
      token = gelesen.token;

      /*
       * ⚠️ **Der Versatz wird hier ausgewertet, nicht nur mitgeschrieben.** Er ist das Mass,
       * das für `creationDate` zählt — danach ordnet Relay die Protokollzeilen, und eine Uhr,
       * die eine Stunde danebengeht, erzeugt ein Protokoll, dessen Reihenfolge niemand mehr
       * herstellen kann. Ab 30 s steht es im Dateilog, ab 120 s sieht es der Prüfer
       * (`platform/clockCheck.ts`, Entscheidung des Nutzers vom 2026-09-16).
       *
       * Ohne `await`: die Anmeldung darf nicht auf ein Dateilog warten.
       */
      void verarbeiteServerversatz(anmeldung.driftSeconds, appendAppLog);

      return anmeldung;
    })();

    try {
      return await laufendeAnmeldung;
    } finally {
      laufendeAnmeldung = null;
    }
  };

  /**
   * Ein angemeldeter Aufruf. Bei `401` wird **genau einmal** neu angemeldet — ein Token kann
   * ablaufen, während die App offline war. Ein zweites `401` ist keine abgelaufene Sitzung
   * mehr, sondern ein falsches Passwort; dann wird es durchgereicht, statt in einer Schleife
   * die Ratenbegrenzung des Servers zu füttern.
   */
  const angemeldet = async (
    pfad: string,
    init: { method: string; headers?: Record<string, string>; body?: string },
    query?: Record<string, string | number | undefined>,
  ): Promise<unknown> => {
    await anmelden();

    const einmal = async (): Promise<Antwort> =>
      roh(
        pfad,
        { ...init, headers: { ...init.headers, authorization: 'Bearer ' + String(token) } },
        query,
      );

    let antwort = await einmal();
    if (antwort.status === 401) {
      token = null;
      anmeldung = null;
      await anmelden();
      antwort = await einmal();
    }
    if (!antwort.ok) throw await fehlerAus(antwort);

    const text = await antwort.text();
    if (text.trim() === '') return null;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      throw new ServerFehler('serverfehler', 'Antwort war kein JSON: ' + text.slice(0, 200));
    }
  };

  return {
    anmelden,

    async health() {
      // Ohne Anmeldung: das Lebenszeichen soll auch dann etwas sagen, wenn die Zugangsdaten
      // falsch sind — sonst kann niemand unterscheiden, ob der Server fehlt oder das Passwort.
      const antwort = await roh('/rest/v1/health/check', { method: 'GET' });
      if (!antwort.ok) throw await fehlerAus(antwort);
      const text = await antwort.text();
      return text.trim() === '' ? null : (JSON.parse(text) as unknown);
    },

    async get<T>(pfad: string, query?: Record<string, string | number | undefined>, kopf?: Record<string, string>) {
      return (await angemeldet(pfad, { method: 'GET', headers: kopf }, query)) as T;
    },

    async post<T>(pfad: string, rumpf: unknown) {
      return (await angemeldet(pfad, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(rumpf),
      })) as T;
    },

    letzteAnmeldung: () => anmeldung,

    vergessen() {
      token = null;
      anmeldung = null;
    },
  };
}
